// /receptora/retorno: where the transmitter returns the operator's browser, shown as the progress of
// the new connection while Power confirms it. The `result` the URL carries is only a hint: it words
// the waiting message and, unless it says authorised, the notice the connection's detail opens
// with; the connection's real status is read from Power before the operator is taken to it.
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Result } from 'effect';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { connectionQueryOptions } from '../client/edge.ts';
import { CallFailure } from '../view/shared.tsx';
import { JourneyFrame } from './frame/journey-frame.tsx';
import { CheckMark } from './wizard/layout/wizard-stepper.tsx';

/** What the transmitter's return says: a hint, never proof. */
export const RETURN_HINTS = ['authorised', 'rejected', 'error'] as const;
export type ReturnHint = (typeof RETURN_HINTS)[number];

/** The hints the connection's detail opens with a notice for: a refusal or an error. */
export const RETURN_NOTICES = ['rejected', 'error'] as const satisfies readonly ReturnHint[];
export type ReturnNotice = (typeof RETURN_NOTICES)[number];

/** The hint a `result` names; anything else is an error to confirm, never a success. */
export const returnHintOf = (result: string): ReturnHint =>
  RETURN_HINTS.find((hint) => hint === result) ?? 'error';

const WAITING: Readonly<Record<ReturnHint, string>> = {
  authorised:
    'A autorização terminou na transmissora. Estamos confirmando com a receptora; isso leva alguns segundos.',
  rejected: 'A transmissora indicou recusa; confirmando com a receptora…',
  error: 'A transmissora indicou um erro; confirmando com a receptora…',
};

// The transmitter's part of the progress, as its return words it; `at` names it once Power answers.
const RETURNED: Readonly<
  Record<ReturnHint, { readonly title: (at: string) => string; readonly about: string }>
> = {
  authorised: {
    title: (at) => `Titular autorizou ${at}`,
    about: 'A transmissora devolveu você ao Admin',
  },
  rejected: {
    title: (at) => `Retorno ${at}`,
    about: 'A transmissora indicou que o titular recusou',
  },
  error: { title: (at) => `Retorno ${at}`, about: 'A transmissora indicou um erro' },
};

// The detail opens with the hint's notice, unless the transmitter said it authorised.
const DETAIL_SEARCH = {
  authorised: {},
  rejected: { result: 'rejected' },
  error: { result: 'error' },
} as const satisfies Readonly<Record<ReturnHint, object>>;

const PROGRESS_STATES = ['done', 'now', 'next'] as const;
type ProgressState = (typeof PROGRESS_STATES)[number];

const PROGRESS_ARIA_CURRENT: Readonly<Record<ProgressState, 'step' | false>> = {
  done: false,
  now: 'step',
  next: false,
};

// A done step's dot holds a check mark; the others are empty.
const PROGRESS_DOTS: Readonly<Record<ProgressState, JSX.Element | null>> = {
  done: <CheckMark />,
  now: null,
  next: null,
};

const Progress = ({
  steps,
}: {
  readonly steps: readonly (readonly [title: string, about: string, state: ProgressState])[];
}): JSX.Element => (
  <ol className="journey-progress" aria-label="Andamento">
    {steps.map(([title, about, state]) => (
      <li key={title} data-state={state} aria-current={PROGRESS_ARIA_CURRENT[state]}>
        <span className="journey-progress-dot">{PROGRESS_DOTS[state]}</span>
        <div>
          <strong>{title}</strong>
          <span>{about}</span>
        </div>
      </li>
    ))}
  </ol>
);

const ReturnCard = ({
  eyebrow,
  title,
  children,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element => (
  <div className="journey-return">
    <div>
      <span className="admin-eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
    </div>
    {children}
    <div className="journey-actions">
      <Link to="/receptora" className="fui-button fui-button-secondary">
        Voltar às conexões
      </Link>
    </div>
    <p className="journey-note">
      Se a transmissora recusou ou deu erro, a conexão abre com um aviso explicando o que aconteceu
      e como tentar de novo.
    </p>
  </div>
);

const Confirming = ({
  connectionId,
  hint,
}: {
  readonly connectionId: string;
  readonly hint: ReturnHint;
}): JSX.Element => {
  const navigate = useNavigate();
  const query = useQuery(connectionQueryOptions(connectionId));
  const detail =
    query.status === 'success' && Result.isSuccess(query.data) ? query.data.success : null;
  const confirmed = detail !== null;

  useEffect(() => {
    if (confirmed) {
      void navigate({
        to: '/receptora/conexoes/$id',
        params: { id: connectionId },
        search: DETAIL_SEARCH[hint],
        replace: true,
      });
    }
  }, [confirmed, connectionId, hint, navigate]);

  // The return URL names only the connection: its transmitter is Power's to say.
  const name = detail?.connection.authorisationServerName ?? null;
  const returned = RETURNED[hint];
  const steps = [
    ['Pedido criado na receptora', 'Conexão registrada e autorização iniciada', 'done'],
    [returned.title(name === null ? 'na transmissora' : `em ${name}`), returned.about, 'done'],
    [
      'Confirmando com a receptora',
      'A situação real vem da receptora, não do retorno da transmissora',
      confirmed ? 'done' : 'now',
    ],
    ['Primeira sincronização', 'Começa sozinha assim que a conexão é confirmada', 'next'],
  ] as const;
  const eyebrow = name === null ? 'Nova conexão' : `Nova conexão · ${name}`;

  if (query.status === 'error') {
    return (
      <ReturnCard eyebrow={eyebrow} title="Não foi possível confirmar">
        <p role="alert">Falha inesperada.</p>
      </ReturnCard>
    );
  }
  if (query.status === 'success' && Result.isFailure(query.data)) {
    return (
      <ReturnCard eyebrow={eyebrow} title="Não foi possível confirmar">
        <CallFailure failure={query.data.failure} />
      </ReturnCard>
    );
  }
  return (
    <ReturnCard eyebrow={eyebrow} title={confirmed ? 'Conexão confirmada' : 'Quase lá'}>
      <output className="journey-lead">
        {confirmed ? 'Abrindo a conexão para você acompanhar a sincronização…' : WAITING[hint]}
      </output>
      <Progress steps={steps} />
    </ReturnCard>
  );
};

/** The return as the URL states it: the connection it names, or none. */
export type ReturnTarget =
  | { readonly kind: 'connection'; readonly connectionId: string; readonly hint: ReturnHint }
  | { readonly kind: 'unidentified' };

export function ReturnPage({ target }: { readonly target: ReturnTarget }): JSX.Element {
  return (
    <JourneyFrame place="Retorno da transmissora">
      {target.kind === 'connection' ? (
        <Confirming connectionId={target.connectionId} hint={target.hint} />
      ) : (
        <ReturnCard eyebrow="Nova conexão" title="Retorno da transmissora">
          <p role="alert">O retorno não identificou a conexão.</p>
        </ReturnCard>
      )}
    </JourneyFrame>
  );
}
